import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, ClipboardList, Download, Search, RotateCw, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import UserActivityDrawer from '../../components/admin/UserActivityDrawer';
import ExplainProrateButton from '../../components/admin/ExplainProrateButton';
import ExplainRenewalButton from '../../components/admin/ExplainRenewalButton';
import Pagination from '../../components/common/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAB_CATEGORY_MAP = {
  plans: ['plan_change'],
  perms: ['role_change', 'capability_change'],
  other: ['user_edit', 'password_reset', 'twofa_reset', 'user_delete'],
};

const SOURCE_LABEL = {
  admin_ui: { label: 'Admin', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  razorpay_webhook: { label: 'Payment', className: 'bg-green-100 text-green-700 border-green-200' },
  auto_expiry: { label: 'Auto-expiry', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  migration: { label: 'Migration', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  system: { label: 'System', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const CAT_LABEL = {
  plan_change: 'Plan',
  role_change: 'Role',
  capability_change: 'Capabilities',
  user_edit: 'Info edit',
  password_reset: 'Password reset',
  twofa_reset: '2FA reset',
  user_delete: 'User delete',
};

const formatTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const UserAuditPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('plans');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [targetSearch, setTargetSearch] = useState('');
  const [actorSearch, setActorSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTarget, setDrawerTarget] = useState(null);
  const [perPage, setPerPage] = useState(50);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    for (const cat of TAB_CATEGORY_MAP[activeTab]) params.append('categories', cat);
    if (targetSearch.trim()) params.set('target_search', targetSearch.trim());
    if (actorSearch.trim()) params.set('actor_search', actorSearch.trim());
    if (fromDate) params.set('from_date', new Date(fromDate).toISOString());
    if (toDate) {
      const d = new Date(toDate); d.setHours(23, 59, 59, 999);
      params.set('to_date', d.toISOString());
    }
    return params;
  }, [activeTab, targetSearch, actorSearch, fromDate, toDate]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      params.set('page', String(page));
      params.set('per_page', String(perPage));
      const res = await axios.get(`${API}/admin/user-audit-events?${params.toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit events');
    } finally {
      setLoading(false);
    }
  }, [buildParams, page, perPage]);

  useEffect(() => { setPage(1); }, [activeTab, targetSearch, actorSearch, fromDate, toDate]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const exportCsv = async () => {
    const params = buildParams();
    try {
      const res = await axios.get(`${API}/admin/user-audit-events/export?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-activity-log-${activeTab}-${Date.now()}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  const clearFilters = () => {
    setTargetSearch(''); setActorSearch(''); setFromDate(''); setToDate('');
  };

  const renderPlanRow = (evt) => {
    const d = evt.details || {};
    return (
      <>
        <td className="p-3 text-sm min-w-0"><span className="font-medium">{d.from_plan || '—'}</span> <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" /> <span className="font-medium text-emerald-700">{d.to_plan || '—'}</span></td>
        <td className="p-3 text-xs text-slate-600">
          {d.to_expires_at ? `Expires ${formatTs(d.to_expires_at)}` : d.from_expires_at ? 'Cleared' : '—'}
        </td>
        <td className="p-3 text-xs text-slate-600">
          {d.amount_paid ? `₹${d.amount_paid}` : ''}
          {d.coupon_code ? <div className="text-emerald-600">Coupon: {d.coupon_code}</div> : null}
          {d.payment_id ? <div className="text-slate-500 truncate max-w-[180px]" title={d.payment_id}>Txn: {d.payment_id}</div> : null}
          {d.order_id ? <div className="text-slate-400 truncate max-w-[180px]" title={d.order_id}>Order: {d.order_id}</div> : null}
          {d.note ? <div className="italic text-slate-500">{d.note}</div> : null}
          {d.prorate_meta ? <div className="mt-1"><ExplainProrateButton meta={d.prorate_meta} /></div> : null}
          {!d.prorate_meta && d.renewal_meta ? <div className="mt-1"><ExplainRenewalButton meta={d.renewal_meta} /></div> : null}
        </td>
      </>
    );
  };

  const renderPermsRow = (evt) => {
    const d = evt.details || {};
    if (evt.category === 'role_change') {
      return (
        <>
          <td className="p-3 text-sm"><Badge className="bg-slate-100 text-slate-700 border-slate-200">Role</Badge></td>
          <td className="p-3 text-sm"><span className="font-medium">{d.from_role}</span> <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" /> <span className="font-medium text-emerald-700">{d.to_role}</span></td>
          <td className="p-3 text-xs text-slate-500">—</td>
        </>
      );
    }
    return (
      <>
        <td className="p-3 text-sm"><Badge className="bg-purple-100 text-purple-700 border-purple-200">Capabilities</Badge></td>
        <td className="p-3 text-xs">
          {d.added?.length ? <div className="text-emerald-700">+ {d.added.join(', ')}</div> : null}
          {d.removed?.length ? <div className="text-rose-600">− {d.removed.join(', ')}</div> : null}
        </td>
        <td className="p-3 text-xs text-slate-500 max-w-[220px] break-words">
          Now: {d.to_perms?.length ? d.to_perms.join(', ') : '(none)'}
        </td>
      </>
    );
  };

  const renderOtherRow = (evt) => {
    const d = evt.details || {};
    return (
      <>
        <td className="p-3 text-sm"><Badge className="bg-slate-100 text-slate-700 border-slate-200">{CAT_LABEL[evt.category] || evt.category}</Badge></td>
        <td className="p-3 text-xs text-slate-600 max-w-[300px]">
          {evt.category === 'user_edit' && d.changed_fields ? Object.entries(d.changed_fields).map(([k, v]) => (
            <div key={k}><span className="font-medium">{k}:</span> {String(v.from ?? '—')} → {String(v.to ?? '—')}</div>
          )) : evt.category === 'user_delete' ? (
            <div>{d.kind === 'restore' ? 'Restored' : d.kind === 'permanent' ? 'Permanent delete' : `Soft delete · Blacklist ${d.blacklist_duration || '—'} days`}</div>
          ) : <span className="text-slate-400">—</span>}
        </td>
        <td className="p-3 text-xs text-slate-500">—</td>
      </>
    );
  };

  return (
    <Layout>
      <div className="page-transition" data-testid="user-audit-page">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" onClick={() => navigate('/admin/tools')} className="text-gray-600" data-testid="user-audit-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin Tools
          </Button>
        </div>

        <Card className="mb-6 border-l-4 border-l-[#0EA5E9] shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0EA5E9]/5 to-[#6366F1]/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-[#0EA5E9] rounded-lg">
                <ClipboardList className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-3xl text-[#0EA5E9]">User Activity Log</CardTitle>
                <CardDescription className="text-base mt-1">
                  Trace every plan change, permission grant, and admin action against any user. Full-admin only.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Filter bar */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Target user (name / email / INDoS)</label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={targetSearch} onChange={(e) => setTargetSearch(e.target.value)} placeholder="e.g. rahul or 24TP1234" className="pl-8" data-testid="user-audit-target-search" />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-600 mb-1 block">Actor (admin who made the change)</label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={actorSearch} onChange={(e) => setActorSearch(e.target.value)} placeholder="admin name / INDoS" className="pl-8" data-testid="user-audit-actor-search" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">From</label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="user-audit-from-date" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">To</label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="user-audit-to-date" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="user-audit-clear-btn">Clear</Button>
                <Button variant="outline" size="sm" onClick={fetchEvents} disabled={loading} data-testid="user-audit-refresh-btn">
                  <RotateCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
                <Button size="sm" onClick={exportCsv} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="user-audit-export-btn">
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="plans" data-testid="user-audit-tab-plans">Plan History</TabsTrigger>
            <TabsTrigger value="perms" data-testid="user-audit-tab-perms">Role &amp; Capabilities</TabsTrigger>
            <TabsTrigger value="other" data-testid="user-audit-tab-other">Other Actions</TabsTrigger>
          </TabsList>

          {['plans', 'perms', 'other'].map((tabKey) => (
            <TabsContent key={tabKey} value={tabKey}>
              <Card>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                        <th className="p-3">When</th>
                        <th className="p-3">Target user</th>
                        <th className="p-3">Source</th>
                        <th className="p-3">Actor</th>
                        {tabKey === 'plans' && (<><th className="p-3">Change</th><th className="p-3">Expiry</th><th className="p-3">Extra</th></>)}
                        {tabKey === 'perms' && (<><th className="p-3">Type</th><th className="p-3">Change</th><th className="p-3">Current state</th></>)}
                        {tabKey === 'other' && (<><th className="p-3">Type</th><th className="p-3">Details</th><th className="p-3"></th></>)}
                      </tr>
                    </thead>
                    <tbody data-testid={`user-audit-tbody-${tabKey}`}>
                      {loading ? (
                        <tr><td colSpan={7} className="p-8 text-center text-slate-500">Loading…</td></tr>
                      ) : items.length === 0 ? (
                        <tr><td colSpan={7} className="p-8 text-center text-slate-500">No events match the current filters.</td></tr>
                      ) : items.map((evt) => {
                        const src = SOURCE_LABEL[evt.source] || { label: evt.source, className: 'bg-slate-100 text-slate-600' };
                        const tgt = evt.target_user || {};
                        const act = evt.actor || {};
                        return (
                          <tr key={evt.id} className="border-b hover:bg-slate-50 cursor-pointer" data-testid={`user-audit-row-${evt.id}`}
                              onClick={() => { setDrawerTarget({ id: tgt.id, name: tgt.name }); setDrawerOpen(true); }}>
                            <td className="p-3 text-xs text-slate-600 whitespace-nowrap">{formatTs(evt.timestamp)}</td>
                            <td className="p-3">
                              <div className="text-sm font-medium text-slate-900">{tgt.name || '(unknown)'}</div>
                              <div className="text-xs text-slate-500">{tgt.indos_number || tgt.email || '—'}</div>
                            </td>
                            <td className="p-3"><Badge className={`${src.className} border`}>{src.label}</Badge></td>
                            <td className="p-3">
                              {act?.id && tgt?.id && act.id === tgt.id ? (
                                <span className="text-xs text-slate-600 italic">User (self)</span>
                              ) : act?.name ? (
                                <>
                                  <div className="text-sm text-slate-900">{act.name}</div>
                                  <div className="text-xs text-slate-500">{act.indos_number || act.role || ''}</div>
                                </>
                              ) : <span className="text-xs text-slate-400 italic">{src.label}</span>}
                            </td>
                            {tabKey === 'plans' && renderPlanRow(evt)}
                            {tabKey === 'perms' && renderPermsRow(evt)}
                            {tabKey === 'other' && renderOtherRow(evt)}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Pagination */}
              {total > 0 && (
                <Pagination
                  currentPage={page}
                  totalItems={total}
                  itemsPerPage={perPage}
                  onPageChange={setPage}
                  onItemsPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                />
              )}
            </TabsContent>
          ))}
        </Tabs>

        <UserActivityDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          targetUserId={drawerTarget?.id}
          targetUserName={drawerTarget?.name}
          allowJumpToFullLog={false}
        />
      </div>
    </Layout>
  );
};

export default UserAuditPage;
